import type { IconName } from 'lucide-react/dynamic';
import { DateTime } from 'luxon';
import { BaseApi } from './base';
import type { Card, CardActionPayload, User } from './types';

interface TaskSelector {
  type: 'task';
  task: string;
}

interface AchievementSelector {
  type: 'achievement';
  achievement: string;
}

interface CardSelector {
  type: 'card';
  card: string;
}

export type SeriesConfigSelector =
  | TaskSelector
  | AchievementSelector
  | CardSelector;

export interface SeriesConfig {
  selectors: SeriesConfigSelector[];
  rule: 'any_of';
  sequenceType: 'sequential' | 'cumulative';
  amount: number;
  repeatable: boolean;
}

export interface Goal {
  _id: string;
  group: string;
  assignedTo: User[];
  title: string;
  icon: IconName;
  description: string;
  config: SeriesConfig;
  rewardCard: Card | string;
  createdBy: string;
  createdAt: DateTime;
  updatedAt: DateTime;
}

export interface GoalCompletionStatus {
  current: number;
  required: number;
  percentage: number;
  isCompleted: boolean;
  lastAchievementDate?: DateTime;
  trackingStartDate: DateTime;
}

export interface UserGoalStatus {
  user: User;
  status: GoalCompletionStatus;
  count: number;
}

export interface GoalWithStatus {
  goal: Goal;
  status: GoalCompletionStatus;
  count: number;
  userStatuses?: UserGoalStatus[];
}

export interface CreateGoalPayload {
  title: string;
  icon: IconName;
  description: string;
  assignedTo: string[];
  config: {
    selectors: (
      | {
          type: 'task';
          task: string;
        }
      | {
          type: 'achievement';
          achievement: string;
        }
      | {
          type: 'card';
          card: string;
        }
    )[];
    rule?: 'any_of';
    sequenceType: 'sequential' | 'cumulative';
    amount: number;
    repeatable: boolean;
  };
  rewardCard: string | CardActionPayload[];
}

export class GoalsApi extends BaseApi {
  public async getAll(): Promise<GoalWithStatus[]> {
    return await this._request<GoalWithStatus[]>('/goals', {
      method: 'GET',
    });
  }

  public async getById(goalId: string): Promise<GoalWithStatus> {
    return await this._request<GoalWithStatus>(`/goal/${goalId}`, {
      method: 'GET',
    });
  }

  public async create(payload: CreateGoalPayload): Promise<GoalWithStatus> {
    return await this._request<GoalWithStatus>('/goal', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async update(
    goalId: string,
    payload: CreateGoalPayload,
  ): Promise<GoalWithStatus> {
    return await this._request<GoalWithStatus>(`/goal/${goalId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async delete(goalId: string): Promise<void> {
    await this._request<void>(`/goal/${goalId}`, {
      method: 'DELETE',
    });
  }

  public async reorder(
    items: Array<{ id: string; position: number }>,
  ): Promise<void> {
    await this._request<void>('/goals/reorder', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
  }
}
